import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { Modal } from 'is24-corecss';
import { css } from '@emotion/css';

import { SolvencyChoice, SolvencyChoiceModalType } from '../schufaCheckMieterPlus/solvencyChoice/solvencyChoice';
import { SolvencyPreview } from './solvencyPreview/solvencyPreview';
import Is24SolvencyCheckDocumentPreview from './is24_solvency_check_document_preview.png';
import Is24SolvencyCheckDigitalPreview from './is24_solvency_check_digital_preview.png';
import type { Benefit } from '@/@types/benefits';
import type { WebPImageElement } from '@/components/common/display/imageSource/ImageSource';
import { ImageSource } from '@/components/common/display/imageSource/ImageSource';
import { Tabs } from '@/components/common/display/tabs/Tabs';
import { GRAY300, GRAY100, GRAY900 } from '@/domain/colors';

import { useGetHasEntitlement } from '@/customHooks/useGetHasEntitlement.hook';
import { EntitlementType } from '@/@types/entitlement';
import type { SolvencyInfoResponse } from '@/@types/solvency';
import { useGetIs24SolvencyStatus, useGetSchufaStatus } from '@/apiHooks/solvencyStatus.hook';
import { useGetSolvencyInfo } from '@/apiHooks/solvency.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

const styles = {
  scaleDown: css`
    width: 60% !important;
    margin-left: 20%;
  `,
  container: css`
    max-width: 1000px;
    width: 100% !important;
    overflow-x: hidden;
    @media (max-width: 668px) {
      position: fixed !important;
      overflow-y: scroll;
      display: flex;
      flex-flow: column;
      top: 0;
    }
    @media (orientation: landscape) and (max-width: 1023px) {
      height: 100vh;
    },
  `,
  modalContentWrapper: css`
    max-height: inherit !important;
    display: flex;
    flex-flow: column;
    @media (min-width: 668px) {
      max-height: calc(100vh - 200px) !important;
    }
  `,
  modalContent: css`
    overflow-y: auto;
    overflow-x: hidden;
  `,
  imageWrapper: {
    position: 'relative',
    height: 320,
    margin: '-24px -32px 2px -32px',
    borderBottom: `1px solid ${GRAY300}`,
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
    '@media (max-width: 668px)': {
      margin: '-16px -32px 2px -32px',
    },
  },
  image: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    fit: {
      contain: {
        objectFit: 'contain',
      },
      cover: {
        objectFit: 'cover',
      },
    },
    margin: '0 auto',
    display: 'block',
  },
  contentWrapper: {
    withButton: {
      '@media (max-width: 1013px)': {
        marginBottom: 72,
      },
    },
    '& [class*="tabs-container"]': {
      '@media (max-width: 668px)': {
        marginBottom: 4,
      },
    },
    '& [role="tablist"]': {
      zIndex: 5,
      justifyContent: 'center',
      top: -2,
      padding: '20px 0 24px 0',
      backgroundColor: GRAY100,
      '@media (max-width: 668px)': {
        padding: '20px 0 16px 0',
        marginBottom: -12,
      },
      '@media (min-width: 669px)': {
        position: 'sticky',
      },
    },
  },
  separator: {
    borderTop: `1px solid ${GRAY300}`,
    marginLeft: -32,
    marginRight: -32,
    backgroundColor: GRAY100,
    '@media (max-width: 1013px)': {
      position: 'fixed',
      zIndex: 6,
      bottom: 0,
      left: 0,
      right: 0,
      boxShadow: `0px 0px 20px -10px ${GRAY900}`,
    },
  },
  buttonWrapper: {
    background: `var(--color-background-default)`,
    maxWidth: 768,
    '@media (orientation: landscape) and (max-width: 1023px)': {
      paddingBottom: 24,
    },
  },
  modalBackDrop: css`
    @media (max-width: 669px) {
      position: initial !important;
    }
    button[aria-label='Schließen'] {
      color: #333;
    }
    @media (orientation: landscape) and (max-width: 1023px) {
      height: 100%;
    }
  `,
} as const;

export interface PersonalData {
  firstName: string;
  lastName: string;
  birthDate?: Date;
}

export type SolvencyChoiceModalProps = {
  title: string | ReactNode;
  isVisible: boolean;
  image: string;
  imageWebp?: WebPImageElement;
  imageMobile?: string;
  imageMobileWebp?: WebPImageElement;
  imageUseFullWidth: boolean;
  imageAlt: string;
  imageBackgroundStyles?: object;
  tabsContent: [JSX.Element, JSX.Element?, JSX.Element?];
  buttonArea?: JSX.Element | null;
  formData?: JSX.Element | null;
  benefit: Benefit;
  solvencyInfo?: SolvencyInfoResponse;
  personalData?: PersonalData;
  onClose: () => void;
};

export const SolvencyChoiceModal = (props: SolvencyChoiceModalProps) => {
  useModalFocus(props.isVisible);
  const camelCasedContentIdentifier = `_${props.benefit.toLowerCase()}ModalContent`.replace(/_./g, (match) =>
    match[1].toUpperCase(),
  );

  const { solvencyInfo } = useGetSolvencyInfo();

  const hasIs24SolvencyEntitlement = useGetHasEntitlement(EntitlementType.IS24_SOLVENCY_CHECK);
  const { schufaStatus } = useGetSchufaStatus();
  const { is24SolvencyStatus } = useGetIs24SolvencyStatus();

  const hasAnySolvencyDocument = !!is24SolvencyStatus?.verificationCode || !!schufaStatus?.verificationCode;
  const [solvencyChoiceModalType, setSolvencyChoiceModalType] = useState<SolvencyChoiceModalType>(
    SolvencyChoiceModalType.SOLVENCY_CHOICE,
  );

  return (
    <Modal.Root isOpen={props.isVisible} onClose={props.onClose}>
      <Modal.Backdrop className={css(styles.modalBackDrop)}>
        <Modal.Surface className={`Modal ${css(styles.container)} ${camelCasedContentIdentifier}`}>
          <Modal.Header>
            <Modal.Title>{<div className={`desk-padding-left-xl`}>{props.title}</div>}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body className={`absolute-reference ${css(styles.modalContentWrapper)}`}>
            {(hasAnySolvencyDocument || !hasIs24SolvencyEntitlement) && (
              <>
                <div className={`${css(styles.modalContent)}`}>
                  <div>
                    {!props.formData && (
                      <div
                        data-testid='ImageWrapper'
                        className={css({ ...styles.imageWrapper, ...props.imageBackgroundStyles })}
                      >
                        <ImageSource
                          image={props.image}
                          imageWebp={props.imageWebp}
                          imageMobile={props.imageMobile}
                          imageMobileWebp={props.imageMobileWebp}
                          imageAlt={props.imageAlt}
                          sizes='(min-width: 1014px) 1000px, 100vw'
                          style={{
                            ...(props.imageUseFullWidth ? styles.image.fit.cover : styles.image.fit.contain),
                            ...styles.image,
                          }}
                        />
                      </div>
                    )}
                    <div
                      className={`${css(styles.contentWrapper, props.buttonArea && styles.contentWrapper.withButton)}`}
                    >
                      {props.formData ? (
                        props.formData
                      ) : (
                        <Tabs tabsContent={props.tabsContent} benefit={props.benefit} />
                      )}
                    </div>
                  </div>
                </div>
                {props.buttonArea && (
                  <div className={css(styles.separator)}>
                    <div
                      className={`horizontal-center palm-padding-bottom-l lap-padding-bottom-l padding-top-l padding-bottom-l ${css(
                        styles.buttonWrapper,
                      )}`}
                    >
                      {props.buttonArea}
                    </div>
                  </div>
                )}
              </>
            )}
            {!hasAnySolvencyDocument && hasIs24SolvencyEntitlement && (
              <>
                {solvencyChoiceModalType === SolvencyChoiceModalType.SOLVENCY_CHOICE && (
                  <SolvencyChoice setSolvencyChoiceModalType={setSolvencyChoiceModalType} solvencyInfo={solvencyInfo} />
                )}
                {solvencyChoiceModalType === SolvencyChoiceModalType.IS24_SOLVENCY_PREVIEW && (
                  <SolvencyPreview onClose={() => setSolvencyChoiceModalType(SolvencyChoiceModalType.SOLVENCY_CHOICE)}>
                    <div className={`grid grid-flex one-whole grid-justify-center ${styles.scaleDown}`}>
                      <img
                        className={'grid-item eleven-twelfth palm-one-whole'}
                        src={Is24SolvencyCheckDocumentPreview}
                        alt={''}
                      />
                      <img
                        className={'grid-item eight-twelfth palm-one-whole padding-top-l palm-padding-top-xl'}
                        src={Is24SolvencyCheckDigitalPreview}
                        alt={''}
                      />
                    </div>
                  </SolvencyPreview>
                )}
              </>
            )}
          </Modal.Body>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  );
};
